import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo, useRef } from 'react';
import { sessionAPI } from '../services/api';
import { useBusiness } from './BusinessContext';
import { useUser } from './UserContext';
import type { Session, AudioSourceType } from '../types/session';

interface SessionContextType {
  session: Session | null;
  /** When set, all OPD form tabs load/save against this session (e.g. editing a past visit). Mic is disabled. */
  formSessionOverride: Session | null;
  /** Session id used by FormDataContext / forms — override wins over active `session`. */
  effectiveFormSessionId: string | null;
  isEditingHistoricalForms: boolean;
  beginFormSessionEdit: (sessionId: string) => Promise<void>;
  endFormSessionEdit: () => void;
  loading: boolean;
  error: string | null;
  createSession: (audioSourceType?: AudioSourceType, cabinId?: string) => Promise<Session | void>;
  completeSession: () => Promise<void>;
  clearSession: () => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [formSessionOverride, setFormSessionOverride] = useState<Session | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { doctor, patient } = useUser();
  const { activeConfigId } = useBusiness();
  // Guard: prevents duplicate visit creation when multiple WorkflowSessionBar instances
  // (one per page tab) all receive the same 'create-session-with-audio' event simultaneously.
  const createSessionInFlightRef = useRef(false);

  const effectiveFormSessionId = useMemo(
    () => formSessionOverride?.session_id ?? session?.session_id ?? null,
    [formSessionOverride?.session_id, session?.session_id]
  );

  const isEditingHistoricalForms = formSessionOverride != null;

  const endFormSessionEdit = useCallback(() => {
    setFormSessionOverride(null);
  }, []);

  const beginFormSessionEdit = useCallback(
    async (sessionId: string) => {
      const res = await sessionAPI.get(sessionId);
      const s = res.data as Session;
      if (
        patient?.patient_id != null &&
        s.patient_id != null &&
        Number(s.patient_id) !== Number(patient.patient_id)
      ) {
        throw new Error('That visit belongs to another patient.');
      }
      setFormSessionOverride(s);
    },
    [patient?.patient_id]
  );

  // Load session from localStorage on mount
  useEffect(() => {
    const savedSession = localStorage.getItem('current_session');
    if (savedSession) {
      try {
        const parsedSession = JSON.parse(savedSession);
        // Only restore if status is active
        if (parsedSession.status === 'active') {
          setSession(parsedSession);
        } else {
          localStorage.removeItem('current_session');
        }
      } catch (err) {
        console.error('Failed to parse saved session:', err);
        localStorage.removeItem('current_session');
      }
    }
  }, []);

  // Refetch session from API when we have session_id but missing visit_id (e.g. restored from older localStorage)
  useEffect(() => {
    if (!session?.session_id || session.visit_id != null) return;
    let cancelled = false;
    sessionAPI
      .get(session.session_id)
      .then((res) => {
        if (!cancelled && res?.data) setSession(res.data as Session);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [session?.session_id, session?.visit_id]);

  // Save session to localStorage whenever it changes
  useEffect(() => {
    if (session) {
      localStorage.setItem('current_session', JSON.stringify(session));
    } else {
      localStorage.removeItem('current_session');
    }
  }, [session]);

  const createSession = useCallback(async (
    audioSourceType: AudioSourceType = 'browser',
    cabinId?: string
  ) => {
    // Synchronous guard: if another call is already in flight (from a parallel WorkflowSessionBar
    // instance receiving the same event), drop this one silently before any await.
    if (createSessionInFlightRef.current) return;
    createSessionInFlightRef.current = true;

    if (!doctor || !patient) {
      createSessionInFlightRef.current = false;
      throw new Error('Please select a doctor and patient first');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await sessionAPI.create({
        clinician_id: doctor.doctor_id,
        patient_id: patient.patient_id,
        mode: 'ambient',
        ...(activeConfigId ? { config_id: activeConfigId } : {}),
        // Hybrid Audio Support
        audio_source_type: audioSourceType,
        cabin_id: cabinId,
      });

      const newSession = response.data;
      setSession(newSession);
      return newSession;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create session';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
      createSessionInFlightRef.current = false;
    }
  }, [doctor, patient, activeConfigId]);

  const completeSession = useCallback(async () => {
    if (!session) {
      throw new Error('No active session');
    }

    setLoading(true);
    setError(null);

    try {
      // Save final transcripts to database before completing session
      const whisperData = (window as any).__whisperLiveKitData;
      if (whisperData?.lines?.length > 0) {
        const realSegments = whisperData.lines.filter((line: any) => line.speaker !== -2 && line.text);
        if (realSegments.length > 0) {
          try {
            await fetch('/api/transcripts/save', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'ngrok-skip-browser-warning': 'true',
              },
              body: JSON.stringify({
                session_id: session.session_id,
                segments: realSegments.map((seg: any) => ({
                  speaker: seg.speaker,
                  text: seg.text,
                  start: typeof seg.start === 'string' ? parseFloat(seg.start) : seg.start,
                  end: typeof seg.end === 'string' ? parseFloat(seg.end) : seg.end,
                  detected_language: seg.detected_language
                }))
              })
            });
            console.log('[SessionContext] Final transcript saved before session end');
          } catch (err) {
            console.error('[SessionContext] Failed to save final transcript:', err);
          }
        }
      }

      await sessionAPI.complete(session.session_id);
      // Clear session completely to return to initial screen
      setSession(null);
      localStorage.removeItem('current_session');
      // Clear WhisperLiveKit data
      delete (window as any).__whisperLiveKitData;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to complete session';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [session]);

  const clearSession = useCallback(() => {
    setSession(null);
    setFormSessionOverride(null);
    localStorage.removeItem('current_session');
  }, []);

  return (
    <SessionContext.Provider
      value={{
        session,
        formSessionOverride,
        effectiveFormSessionId,
        isEditingHistoricalForms,
        beginFormSessionEdit,
        endFormSessionEdit,
        loading,
        error,
        createSession,
        completeSession,
        clearSession,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}
